import React, { useMemo, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { PCB_CORE_URL } from "../config";
import "./quotes.css";
import { getAccessToken } from "../authSession";

const API_BASE = (PCB_CORE_URL || "").replace(/\/+$/, "");

function money(v) {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

const STATUS_FR = {
  draft: "Brouillon",
  pending: "En attente",
  validated: "Validé",
  rejected: "Rejeté",
  paid: "Payé",
  in_production: "En production",
  shipped: "Expédié",
  completed: "Terminé",
  cancelled: "Annulé",
};

function statusFr(status) {
  if (!status) return "-";
  return STATUS_FR[String(status).toLowerCase()] || String(status);
}

function money3(v) {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

function lineType(label) {
  const s = String(label || "").toLowerCase();
  if (s.includes("pcb") || s.includes("fabrication")) return "pcb";
  if (s.includes("assembl")) return "assembly";
  if (s.includes("composant") || s.includes("component")) return "components";
  if (s.includes("frais fixes") || s.includes("fixed fee") || s.includes("fee")) return "fixed_fee";
  if (s.includes("expedi") || s.includes("shipp") || s.includes("livr")) return "shipping";
  return "other";
}

function marginRatioForType(q, type) {
  if (type === "pcb") return Number(q?.pcb_margin_pct || 0);
  if (type === "assembly") return Number(q?.assembly_margin_pct || 0);
  if (type === "components") return Number(q?.components_margin_pct || 0);
  return 0;
}

function computeQuoteBreakdown(q) {
  const fallbackByType = (q?.lines || []).reduce(
    (acc, line) => {
      const type = lineType(line?.label);
      const amount = Number(line?.amount || 0);
      if (type === "pcb") acc.pcb += amount;
      else if (type === "assembly") acc.assembly += amount;
      else if (type === "components") acc.components += amount;
      else if (type === "shipping") acc.shipping += amount;
      else if (type === "fixed_fee") acc.fixedFee += amount;
      return acc;
    },
    { pcb: 0, assembly: 0, components: 0, shipping: 0, fixedFee: 0 }
  );

  const subtotalPcb = Number(q?.subtotal_pcb ?? fallbackByType.pcb);
  const subtotalAssembly = Number(q?.subtotal_assembly ?? fallbackByType.assembly);
  const subtotalComponents = Number(q?.subtotal_components ?? fallbackByType.components);
  const subtotalShipping = Number(q?.subtotal_shipping ?? q?.shipping ?? fallbackByType.shipping);
  const subtotalFixedFee = Number(q?.pricing_meta?.fixed_fee ?? fallbackByType.fixedFee);
  const pcbMarginRatio = Number(q?.pcb_margin_pct || 0);
  const assemblyMarginRatio = Number(q?.assembly_margin_pct || 0);
  const componentsMarginRatio = Number(q?.components_margin_pct || 0);
  const pcbAfterMargin = subtotalPcb * (1 + pcbMarginRatio);
  const assemblyAfterMargin = subtotalAssembly * (1 + assemblyMarginRatio);
  const componentsAfterMargin = subtotalComponents * (1 + componentsMarginRatio);
  const shippingAfterMargin = subtotalShipping;
  const fixedFeeAfterMargin = subtotalFixedFee;
  const totalBeforeDiscount = pcbAfterMargin + assemblyAfterMargin + componentsAfterMargin + shippingAfterMargin + fixedFeeAfterMargin;
  const totalHt = Number(q?.total || 0);
  const discountAmount = Math.max(0, totalBeforeDiscount - totalHt);
  const vatAmount = Number(q?.vat_amount || 0);
  const totalTtc = Number(q?.total_ttc || q?.total || 0);
  return {
    subtotalPcb,
    subtotalAssembly,
    subtotalComponents,
    subtotalShipping,
    subtotalFixedFee,
    pcbMarginRatio,
    assemblyMarginRatio,
    componentsMarginRatio,
    pcbAfterMargin,
    assemblyAfterMargin,
    componentsAfterMargin,
    shippingAfterMargin,
    fixedFeeAfterMargin,
    totalBeforeDiscount,
    discountAmount,
    totalHt,
    vatAmount,
    totalTtc,
  };
}

function buildInvoiceRows(q) {
  const qtyBoards = Number(q?.pricing_meta?.pcb_qty || q?.project_quantity || 1) || 1;
  const vatPct = Number(q?.vat_rate_percent || 0);
  const rows = (q?.lines || [])
    .filter((l) => lineType(l?.label) !== "fixed_fee")
    .map((l) => {
      const type = lineType(l?.label);
      const rawHt = Number(l?.amount || 0);
      const marginRatio = marginRatioForType(q, type);
      const totalHt = rawHt * (1 + marginRatio);
      let qty, unitHt;
      if (type === "pcb") {
        qty = qtyBoards;
        unitHt = qtyBoards > 0 ? totalHt / qtyBoards : totalHt;
      } else {
        qty = 1;
        unitHt = totalHt;
      }

      return {
        description: l?.label || "Ligne",
        type,
        tva: vatPct,
        unitHt,
        qty,
        discount: 0,
        totalHt,
        rawHt,
        marginRatio,
      };
    });
  return rows;
}

function normalizeDecisions(pricingMeta) {
  const ld = pricingMeta?.line_decisions || {};
  const arr = Object.values(ld || {}).map((x) => ({
    mpn: x?.mpn ?? "",
    buy_qty: Number(x?.buy_qty ?? 0),
    planned_qty: Number(x?.planned_qty ?? x?.qty_to_order ?? x?.buy_qty ?? 0),
    qty_to_order: Number(x?.qty_to_order ?? x?.planned_qty ?? x?.buy_qty ?? 0),
    chosen: x?.chosen ?? null,
    reason: x?.reason ?? null,
  }));

  arr.sort((a, b) => {
    const ab = a.reason ? 1 : 0;
    const bb = b.reason ? 1 : 0;
    if (ab !== bb) return ab - bb;
    return String(a.mpn || "").localeCompare(String(b.mpn || ""));
  });

  return { ok: arr.filter((x) => !x.reason), blocked: arr.filter((x) => !!x.reason) };
}

function canReplace(reason) {
  const r = String(reason || "").toLowerCase();
  return r.includes("no_price") || r.includes("invalid") || r.includes("stock");
}

function reasonClass(reason) {
  const r = String(reason || "").toLowerCase();
  if (r.includes("no_price")) return "qm-reason--noprice";
  if (r.includes("invalid")) return "qm-reason--invalid";
  if (r.includes("stock")) return "qm-reason--stock";
  return "qm-reason--other";
}

function copyToClipboard(text) {
  try {
    navigator.clipboard.writeText(String(text || ""));
  } catch (_e) {
    const ta = document.createElement("textarea");
    ta.value = String(text || "");
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  }
}

// eslint-disable-next-line no-unused-vars
const ORDER_PROFESSIONAL_CONFIRMATION_TEXT =
  "Je reconnais agir en qualité de professionnel, avoir lu et accepté les Conditions Générales de Vente (CGV), et comprends que cette commande concerne une prestation d'assemblage électronique réalisée sur mesure ne bénéficiant d'aucun droit de rétractation.";

const ORDER_VALIDATION_NOTICE =
  "La validation de la commande entraîne le lancement immédiat des approvisionnements et de la préparation de production. La commande devient ferme et définitive.";

export function OrderValidationModal({ open, accepting = false, onClose, onConfirm }) {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (open) setChecked(false);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="qm-backdrop" onClick={accepting ? undefined : onClose} style={{ zIndex: 100000 }}>
      <div className="qm-modal qm-modal--legal" onClick={(e) => e.stopPropagation()}>
        <div className="qm-header">
          <div>
            <div className="qm-title-row">
              <div className="qm-title">Validation de commande</div>
            </div>
            <div className="qm-sub">Avant paiement</div>
          </div>
          <button className="qm-icon-btn" onClick={onClose} aria-label="Fermer" disabled={accepting}>X</button>
        </div>
        <div className="qm-section">
          <label className="qm-legal-check">
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} disabled={accepting} />
            <span>
              {"Je reconnais agir en qualité de professionnel, avoir lu et accepté les "}
              <a href="https://eviacore.com/cgv/" target="_blank" rel="noreferrer">
                {" Conditions Générales de Vente (CGV),"}
              </a>
              {", et comprends que cette commande concerne une prestation d'assemblage électronique réalisée sur mesure ne bénéficiant d'aucun droit de rétractation."}
            </span>
            <span style={{ display: "none" }}>
              Je reconnais agir en qualité de professionnel, avoir lu et accepté les{" "}
              <a href="https://eviacore.com/cgv/" target="_blank" rel="noreferrer">
                Conditions Générales de Vente (CGV)
              </a>
              , et comprends que cette commande concerne une prestation d'assemblage électronique réalisée sur mesure ne bÃ©nÃ©ficiant d'aucun droit de rétractation.
            </span>
          </label>
          <div className="qm-alert" style={{ margin: "14px 0 0 0" }}>{ORDER_VALIDATION_NOTICE}</div>
          <div className="qm-actions" style={{ padding: 0, borderBottom: "none", marginTop: 14 }}>
            <button className="qm-btn qm-btn--ghost" onClick={onClose} disabled={accepting}>Annuler</button>
            <button className="qm-btn qm-btn--primary" onClick={() => onConfirm?.()} disabled={!checked || accepting}>
              {accepting ? "Validation..." : "Valider le devis"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function BankTransferModal({ open, onClose, data }) {
  if (!open) return null;

  const d = data || {};
  const lines = [
    { label: "Bénéficiaire", value: d.beneficiary },
    { label: "Banque", value: d.bank_name },
    { label: "IBAN", value: d.iban },
    { label: "BIC/SWIFT", value: d.bic },
    { label: "Référence", value: d.reference },
    { label: "Montant", value: d.amount != null ? `${money(d.amount)} ${d.currency || "EUR"}` : "-" },
  ];

  return createPortal(
    <div className="qm-backdrop" onClick={onClose} style={{ zIndex: 100000 }}>
      <div className="qm-modal qm-modal--bank" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "760px", height: "auto", maxHeight: "88vh" }}>
        <div className="qm-header">
          <div>
            <div className="qm-title-row">
              <div className="qm-title">Paiement par virement</div>
            </div>
          </div>
          <button className="qm-icon-btn" onClick={onClose} aria-label="Fermer">X</button>
        </div>

        <div className="qm-section">
          <div className="qm-h3">Coordonnées bancaires</div>

          <div className="qm-list">
            {lines.map((x) => (
              <div key={x.label} className="qm-row" style={{ alignItems: "flex-start" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="qm-muted" style={{ fontWeight: 800, marginBottom: 4 }}>{x.label}</div>
                  <div style={{ fontWeight: 800, color: "inherit", wordBreak: "break-word" }}>{x.value || "-"}</div>
                </div>

                <button
                  className="qm-btn qm-btn--small"
                  onClick={() => copyToClipboard(x.value)}
                  disabled={!x.value}
                  title="Copier"
                  style={{ whiteSpace: "nowrap" }}
                >
                  Copier
                </button>
              </div>
            ))}
          </div>

          <div className="qm-alert" style={{ margin: "14px 0 0 0" }}>
            Après virement, le paiement sera validé à réception.
          </div>

          <div className="qm-actions" style={{ padding: 0, borderBottom: "none", marginTop: 12 }}>
            <button className="qm-btn qm-btn--ghost" onClick={onClose}>Fermer</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function QuoteModal({ open, onClose, quote, onFixBom, projectId }) {
  const [tab, setTab] = useState("blocked");
  const [paying, setPaying] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [payMenuOpen, setPayMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [bankOpen, setBankOpen] = useState(false);
  const [bankData, setBankData] = useState(null);
  const [orderValidationOpen, setOrderValidationOpen] = useState(false);
  const [estimateFileUrl, setEstimateFileUrl] = useState("");
  const [quoteState, setQuoteState] = useState(quote || {});

  const token = getAccessToken();

  const downloadWithAuth = async (url, filename) => {
    try {
      const resp = await axios.get(url, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const href = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Erreur téléchargement");
    }
  };
  useEffect(() => {
    setQuoteState(quote || {});
  }, [quote]);

  const q = quoteState || {};
  const pricingMeta = q?.pricing_meta || null;
  const trackingNumber = String(pricingMeta?.shipping_tracking?.tracking_number || "").trim();
  const isPaidQuote = ["paid", "payé", "payée"].includes(String(q?.status || "").trim().toLowerCase());
  const bankTransferOnly = Boolean(trackingNumber) && !isPaidQuote;
  const acceptanceUi = q?.customer_acceptance_ui || {};
  const { ok } = useMemo(() => normalizeDecisions(pricingMeta), [pricingMeta]);

  const blockingList = q?.blocking || pricingMeta?.blocking || [];
  const breakdown = useMemo(() => computeQuoteBreakdown(q), [q]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const qid = quote?.quote_id ?? quote?.id;
    if (!qid || !token) return;
    axios
      .get(`${API_BASE}/user/quotes/${qid}/estimate/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setEstimateFileUrl(res.data?.pennylane_estimate_file_url || ""))
      .catch(() => {});
  }, [open, quote, token]);

  useEffect(() => {
    if (open) setTab(blockingList.length ? "blocked" : "ok");
  }, [open, blockingList.length]);

  useEffect(() => {
    function onDocDown(e) {
      if (!payMenuOpen || !menuRef.current) return;
      if (menuRef.current.contains(e.target)) return;
      setPayMenuOpen(false);
    }
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [payMenuOpen]);

  async function handlePayStripe() {
    try {
      const quoteId = q?.quote_id ?? q?.id;
      if (!quoteId) throw new Error("quote_id manquant");
      if (q?.is_partial) throw new Error("Devis partiel : corrige la BOM avant de payer.");

      setPaying(true);
      const { data } = await axios.post(
        `${API_BASE}/user/quotes/${quoteId}/checkout/`,
        {},
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );

      if (!data?.checkout_url) throw new Error("checkout_url manquant dans la réponse");
      window.location.href = data.checkout_url;
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Erreur checkout");
    } finally {
      setPaying(false);
      setPayMenuOpen(false);
    }
  }

  async function fetchBankTransferDetails() {
    try {
      const quoteId = q?.quote_id ?? q?.id;
      if (!quoteId) throw new Error("quote_id manquant");
      if (q?.is_partial) throw new Error("Devis partiel : corrige la BOM avant de payer.");

      setPaying(true);
      const { data } = await axios.post(
        `${API_BASE}/user/quotes/${quoteId}/bank-transfer/`,
        {},
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );

      if (!data?.reference) throw new Error("Référence manquante (bank-transfer)");
      setBankData(data);
      setBankOpen(true);
      return true;
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Erreur virement");
      return false;
    } finally {
      setPaying(false);
      setPayMenuOpen(false);
    }
  }

  async function handleAcceptQuote() {
    try {
      const quoteId = q?.quote_id ?? q?.id;
      if (!quoteId) throw new Error("quote_id manquant");

      setAccepting(true);
      const { data } = await axios.post(
        `${API_BASE}/user/quotes/${quoteId}/accept/`,
        { professional_confirmation: true },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );

      if (data?.quote) setQuoteState(data.quote);
      if (data?.pennylane_quote?.file_url) setEstimateFileUrl(data.pennylane_quote.file_url);
      setOrderValidationOpen(false);
      alert("Devis validé. Le bon de commande vous sera envoyé par email.");
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Erreur validation du devis");
    } finally {
      setAccepting(false);
    }
  }

  if (!open) return null;

  const modal = (
    <>
      <div className="qm-backdrop" onClick={onClose}>
        <div className="qm-modal" onClick={(e) => e.stopPropagation()}>
          <div className="qm-header">
            <div>
              <div className="qm-title-row">
                <div className="qm-title">Devis généré</div>
                <span className={`qm-badge ${q?.is_partial ? "qm-badge--warn" : "qm-badge--ok"}`}>{q?.is_partial ? "Partiel" : "Complet"}</span>
                <span className="qm-badge qm-badge--draft">{statusFr(q?.status)}</span>
              </div>
              <div className="qm-sub">Devis {q?.quote_id ?? q?.id}</div>
            </div>
            <button className="qm-icon-btn" onClick={onClose} aria-label="Fermer" disabled={paying}>X</button>
          </div>

          <div className="qm-content">
            <div className="qm-left">
              {q?.is_partial ? (
                <div className="qm-alert">
                  <b>Devis partiel</b>
                  <div className="qm-muted" style={{ marginTop: 6 }}>Corrige la BOM pour obtenir un devis final.</div>
                </div>
              ) : (
                <div className="qm-ok">Devis complet</div>
              )}

              <div className="qm-invoice-wrap">
                <table className="qm-invoice">
                  <thead>
                    <tr><th>Description</th><th>TVA</th><th>P.U. HT</th><th>Qté</th><th>Total HT</th></tr>
                  </thead>
                  <tbody>
                    {buildInvoiceRows(q).map((r, idx) => {
                      const isBlocked = r.type === "components" && q?.is_partial && blockingList.length > 0;
                      const isShippingFree = r.type === "shipping" && r.totalHt === 0;
                      return (
                        <tr key={idx} className={isBlocked ? "qm-invoice-row--blocked" : "qm-invoice-row--ok"}>
                          <td>{r.description}</td>
                          <td>{isShippingFree ? "—" : `${money(r.tva)}%`}</td>
                          <td>{isBlocked ? "—" : isShippingFree ? "Gratuit" : money(r.unitHt)}</td>
                          <td>{isBlocked ? "—" : isShippingFree ? "—" : r.qty}</td>
                          <td>{isBlocked ? <span className="qm-invoice-blocked-msg">Corriger les composants</span> : isShippingFree ? <b>Gratuit</b> : <b>{money(r.totalHt)} EUR</b>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* <div className="qm-summary">
                <div className="qm-summary-title">Récapitulatif du calcul</div>
                <div className="qm-summary-grid">
                  <div className="qm-summary-row"><span>HT brut PCB</span><strong>{money(breakdown.subtotalPcb)} EUR</strong></div>
                  <div className="qm-summary-row"><span>HT brut assemblage</span><strong>{money(breakdown.subtotalAssembly)} EUR</strong></div>
                  <div className="qm-summary-row"><span>HT brut composants</span><strong>{money(breakdown.subtotalComponents)} EUR</strong></div>
                  <div className="qm-summary-row"><span>HT brut shipping</span><strong>{money(breakdown.subtotalShipping)} EUR</strong></div>
                  <div className="qm-summary-row"><span>Frais fixes</span><strong>{money(breakdown.subtotalFixedFee)} EUR</strong></div>
                  <div className="qm-summary-row"><span>Après marge PCB</span><strong>{money(breakdown.pcbAfterMargin)} EUR</strong></div>
                  <div className="qm-summary-row"><span>Après marge assemblage</span><strong>{money(breakdown.assemblyAfterMargin)} EUR</strong></div>
                  <div className="qm-summary-row"><span>Après marge composants</span><strong>{money(breakdown.componentsAfterMargin)} EUR</strong></div>
                  <div className="qm-summary-row"><span>HT avant remise</span><strong>{money(breakdown.totalBeforeDiscount)} EUR</strong></div>
                  <div className="qm-summary-row"><span>Remise appliquée</span><strong>{money(breakdown.discountAmount)} EUR</strong></div>
                  <div className="qm-summary-row qm-summary-row--strong"><span>Total HT final</span><strong>{money(breakdown.totalHt)} EUR</strong></div>
                  <div className="qm-summary-row"><span>TVA ({money(q?.vat_rate_percent || 0)}%)</span><strong>{money(breakdown.vatAmount)} EUR</strong></div>
                  <div className="qm-summary-row qm-summary-row--strong qm-summary-row--total"><span>Total TTC</span><strong>{money(breakdown.totalTtc)} EUR</strong></div>
                </div>
              </div> */}

              <div className="qm-actions">
                <button className="qm-btn qm-btn--ghost" onClick={onClose} disabled={paying || accepting}>Fermer</button>

                {estimateFileUrl && (
                  <a
                    href={estimateFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="qm-btn qm-btn--ghost"
                    style={{ textDecoration: "none" }}
                    title="Voir le devis officiel Pennylane"
                  >
                    Voir le devis
                  </a>
                )}

                {/* {(q?.quote_id ?? q?.id) && (
                  <button
                    className="qm-btn qm-btn--ghost"
                    title="Télécharger le devis PDF"
                    disabled={paying}
                    onClick={() => {
                      const qid = q?.quote_id ?? q?.id;
                      downloadWithAuth(`${API_BASE}/user/quotes/${qid}/pdf/`, `devis_${qid}.pdf`);
                    }}
                  >
                    Télécharger PDF
                  </button>
                )} */}


                {!q?.is_partial && acceptanceUi?.can_accept && !isPaidQuote && (
                  <div style={{ display: "grid", gap: 8 }}>
                    <div className="qm-muted">En validant le devis, une confirmation de commande vous sera envoyée par email.</div>
                    <button className="qm-btn qm-btn--primary" onClick={() => setOrderValidationOpen(true)} disabled={paying || accepting}>
                      {accepting ? "Validation..." : (acceptanceUi?.button_label || "Valider le devis")}
                    </button>
                  </div>
                )}

                {!q?.is_partial && !acceptanceUi?.can_accept && acceptanceUi?.helper_text && !q?.is_validated_by_customer && (
                  <div className="qm-muted">{acceptanceUi.helper_text}</div>
                )}

                {!q?.is_partial && q?.is_validated_by_backoffice && q?.is_validated_by_customer && !isPaidQuote && (
                  bankTransferOnly ? (
                    <div style={{ display: "grid", gap: 8 }}>
                      <div className="qm-muted">Paiement différé : règlement par virement bancaire uniquement.</div>
                      <button className="qm-btn qm-btn--primary" onClick={fetchBankTransferDetails} disabled={paying || accepting}>
                        {paying ? "Traitement..." : "Payer par virement bancaire"}
                      </button>
                    </div>
                  ) : (
                    <div style={{ position: "relative" }} ref={menuRef}>
                      <button className="qm-btn qm-btn--primary" onClick={() => setPayMenuOpen((v) => !v)} disabled={paying || accepting} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        {paying ? "Traitement..." : "Payer"}
                        <span style={{ opacity: 0.9 }}></span>
                      </button>

                      {payMenuOpen && !paying && (
                        <div style={{ position: "absolute", top: "calc(100% + 8px)", left: 0, minWidth: 240, background: "#fff", border: "1px solid rgba(0,0,0,0.12)", borderRadius: 12, boxShadow: "0 18px 50px rgba(0,0,0,0.18)", padding: 8, zIndex: 100001 }}>
                          <button className="qm-btn" style={{ width: "100%", justifyContent: "flex-start" }} onClick={handlePayStripe}>Carte (Stripe)</button>
                          <button className="qm-btn" style={{ width: "100%", justifyContent: "flex-start", marginTop: 6 }} onClick={fetchBankTransferDetails}>Virement bancaire</button>
                        </div>
                      )}
                    </div>
                  )
                )}

                {!q?.is_partial && !q?.is_validated_by_backoffice && (<div className="qm-muted">Paiement indisponible : le backoffice doit d'abord valider ce devis.</div>)}
                {!q?.is_partial && q?.is_validated_by_backoffice && !q?.is_validated_by_customer && (
                  <div className="qm-muted">Paiement indisponible : le client doit d'abord valider le devis.</div>
                )}

                {q?.is_partial && (
                  <button className="qm-btn qm-btn--primary" onClick={() => { if (projectId) localStorage.setItem(`pcb_auto_price_${projectId}`, "1"); onClose?.(); onFixBom?.(); }} disabled={paying || accepting}>Corriger la BOM</button>
                )}
              </div>
            </div>

            <div className="qm-right">
              <div className="qm-tabs">
                <button className={`qm-tab ${tab === "blocked" ? "qm-tab--active" : ""}`} onClick={() => setTab("blocked")} disabled={paying}>Bloquantes ({blockingList.length})</button>
                <button className={`qm-tab ${tab === "ok" ? "qm-tab--active" : ""}`} onClick={() => setTab("ok")} disabled={paying}>OK ({ok.length})</button>
              </div>

              <div className="qm-pane">
                {tab === "blocked" ? (
                  blockingList.length === 0 ? (
                    <div className="qm-empty">Aucun composant bloquant.</div>
                  ) : (
                    <div className="qm-list">
                      {blockingList.slice(0, 200).map((b, i) => (
                        <div key={i} className="qm-row qm-row--blocked">
                          <div className="qm-mpn">{b.mpn}</div>
                          <span className={`qm-reason ${reasonClass(b.reason)}`}>{b.reason}</span>
                          {canReplace(b.reason) && (
                            <button className="qm-btn qm-btn--small" onClick={() => { if (projectId) localStorage.setItem(`pcb_auto_price_${projectId}`, "1"); onClose?.(); onFixBom?.(); }} title="Remplacer ce MPN" disabled={paying}>Remplacer</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )
                ) : ok.length === 0 ? (
                  <div className="qm-empty">Aucune ligne validée.</div>
                ) : (
                  <div className="qm-table-wrap">
                    <table className="qm-table">
                      <thead><tr><th>MPN</th><th>Qté achetée</th><th>Prix unit.</th><th>Total HT</th><th>Disponibilité</th></tr></thead>
                      <tbody>
                        {ok.slice(0, 300).map((d, idx) => {
                          const unitRaw = d?.chosen?.unit_price;
                          const displayQty = Number(d?.planned_qty || d?.qty_to_order || d?.buy_qty || 0);
                          const marginRatio = Number(q?.components_margin_pct || 0);
                          const unitWithMargin = unitRaw != null ? Number(unitRaw) * (1 + marginRatio) : null;
                          const totalLine = unitWithMargin != null ? unitWithMargin * displayQty : null;
                          return (
                            <tr key={idx} className="qm-table-row--ok">
                              <td className="qm-td-mpn">{d.mpn}</td>
                              <td>{displayQty || d.buy_qty}</td>
                              <td>{unitWithMargin != null ? money3(unitWithMargin) : "-"}</td>
                              <td>{totalLine != null ? money(totalLine) : "-"}</td>
                              <td>{d?.chosen?.availability != null ? d.chosen.availability : "-"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <BankTransferModal
        open={bankOpen}
        onClose={() => setBankOpen(false)}
        data={bankData}
      />
      <OrderValidationModal
        open={orderValidationOpen}
        accepting={accepting}
        onClose={() => setOrderValidationOpen(false)}
        onConfirm={handleAcceptQuote}
      />
    </>
  );

  return createPortal(modal, document.body);
}





